import { useDafTracking, getDafTrackingProperties } from "@/canvas/features/FormV2/hooks/useDafTracking";
import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

import {
  convertCountryCodeToCountryName,
  FormType,
  PaymentMethod,
  RouteBuilders,
  parseFormBackgroundTheme,
} from "@simplyk/common";

import { useFormV2Context, DonationPaymentInput } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getThankYouPageUrl, navigateAfterPayment } from "@/common/helpers/thankYouPage";
import { inferRouterOutputs } from "@trpc/server";
import { AppRouterType, trpc } from "@/common/helpers/trpc";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useIsEmbeddedModal } from "@/common/hooks/useEmbeddedModal";
import { removeSearchParams, useUrlBuilder } from "@/common/hooks/useUrlBuilder";

export const useDafPaymentFlow = (token: string, isResume: boolean, emailUpdateToken?: string) => {
  const { form, formObject, setCurrentStep } = useFormV2Context(FormType.DonationForm);
  const {
    setDafFlow,
    setSelectedPaymentMethod,
    setDisplayedFormAmount,
    setStripeRecurrenceInterval,
    organization,
    formData,
    category,
    themeColor,
    isEmbed,
    embedId,
    postTransactionUrl,
  } = useContext(FrontendFormContext);
  const searchParams = useSearchParams();
  const isEmbeddedModal = useIsEmbeddedModal(Object.fromEntries(searchParams.entries()));
  const { isoLocale } = useLocaleContext();
  const { trackDaf } = useDafTracking();
  const buildUrl = useUrlBuilder();
  const { isPhoneScreen } = useMediaQuery();
  const { data, error } = trpc.getDafPayment.useQuery(
    { token },
    { enabled: Boolean(token), refetchOnWindowFocus: false }
  );
  const { mutateAsync: confirm, isPending: isConfirming } = trpc.confirmDafDonation.useMutation();
  const { mutateAsync: cancel, isPending: isCanceling } = trpc.cancelDafDonation.useMutation();
  const [actionError, setActionError] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(isResume);
  const hasOpenedProvider = useRef(false);
  const [providerWindowOpened, setProviderWindowOpened] = useState(false);
  const initialized = useRef(false);

  const stage =
    data && data.state === "pending"
      ? data.isSupported
        ? showConfirmation
          ? "supported-confirmation"
          : "supported-help"
        : "custom-details"
      : null;
  const lastTrackedStage = useRef<string | null>(null);
  const trackAction = (action: string) => {
    if (data && data.state !== "deleted") {
      trackDaf("DAF CTA Clicked", {
        ...getDafTrackingProperties(data.details.providerId),
        transactionId: data.transactionId,
        stage,
        action,
        isResume,
      });
    }
  };
  // DAF screens change in place, without a router page-view event.
  useEffect(() => {
    if (!stage || lastTrackedStage.current === stage || !data || data.state === "deleted") {
      return;
    }
    lastTrackedStage.current = stage;
    trackDaf("DAF Step Viewed", {
      ...getDafTrackingProperties(data.details.providerId),
      transactionId: data.transactionId,
      stage,
      isResume,
    });
  }, [stage, data, isResume, trackDaf]);

  const openProvider = (isAutomatic = false) => {
    if (!data || data.state === "deleted" || !data.providerUrl) {
      return;
    }
    if (!isAutomatic) {
      trackAction("open-provider");
    }
    const features = isPhoneScreen
      ? undefined
      : `popup,width=${Math.max(800, window.screen.width - 576)},height=${window.screen.height - 80}`;
    const portal = window.open(data.providerUrl, isPhoneScreen ? "_blank" : "daf-provider", features);
    if (portal) {
      const isolatedPortal: { opener: Window | null } = portal;
      isolatedPortal.opener = null;
      hasOpenedProvider.current = true;
      setProviderWindowOpened(true);
    }
  };

  // Restore emailed checkouts only in the browser; form HTML is publicly cached without query parameters.
  useEffect(() => {
    if (!data || initialized.current) {
      return;
    }
    initialized.current = true;
    if (data.state === "deleted") {
      window.history.replaceState(null, "", buildUrl({ searchParams: removeSearchParams("dafToken") }));
      setDafFlow(null);
      setCurrentStep("selection");
      return;
    }
    if (data.state === "confirmed") {
      window.location.href = buildUrl({
        route: RouteBuilders.buildDonorHomePageUrl({ isoLocale }),
        searchParams: { transactionId: data.transactionId },
        includeRouterSearchParams: false,
      });
      return;
    }
    if (isResume) {
      form.reset(restoreCheckout(data, form.getValues()));
      setDisplayedFormAmount(data.amount);
      setStripeRecurrenceInterval(null);
      setSelectedPaymentMethod(PaymentMethod.Daf);
    } else if (data.isSupported && !isPhoneScreen) {
      openProvider(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Initialize one signed checkout once, retaining edits when returning from the portal.
  }, [data]);

  // Provider portals have no callback; refocusing Zeffy is the donor's return signal.
  useEffect(() => {
    const onReturn = () => {
      if (hasOpenedProvider.current && document.visibilityState === "visible") {
        setShowConfirmation(true);
      }
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, []);

  const onConfirm = async () => {
    if (!data || data.state === "deleted" || isCompleting || isCanceling) {
      return;
    }
    trackAction("confirm");
    setActionError(false);
    setIsCompleting(true);
    const result = await confirm({ token }).catch(() => null);
    if (!result?.data) {
      setActionError(true);
      setIsCompleting(false);
      return;
    }
    await navigateAfterPayment(
      getThankYouPageUrl({
        command: {
          totalAmount: data.amount,
          productsAmount: data.amount,
          tipAmount: 0,
          eligibleAmount: 0,
          paymentMethod: PaymentMethod.Daf,
          email: data.checkout.email,
        },
        formType: FormType.DonationForm,
        organization,
        postTransactionUrl,
        isEmbeddedModal,
        generateETicket: false,
        emailUpdateToken,
        formData,
        isoLocale,
        category,
        isAuction: false,
        themeColor,
        isDarkMode: Boolean(formObject.isDarkMode),
        backgroundTheme: parseFormBackgroundTheme(formObject.backgroundTheme),
        isEmbed,
        embedId,
      })
    ).catch(() => {
      setActionError(true);
      setIsCompleting(false);
    });
  };

  const onBack = async () => {
    trackAction("back");
    setActionError(false);
    const result = await cancel({ token }).catch(() => null);
    if (!result?.data) {
      setActionError(true);
      return;
    }
    window.history.replaceState(null, "", buildUrl({ searchParams: removeSearchParams("dafToken") }));
    setSelectedPaymentMethod(PaymentMethod.Daf);
    setDafFlow(null);
    setCurrentStep("payment");
  };

  return {
    data,
    error,
    actionError,
    showConfirmation,
    providerWindowOpened,
    isPhoneScreen,
    openProvider: () => openProvider(),
    trackAction,
    onConfirm,
    onBack,
    isBusy: isCompleting || isConfirming || isCanceling,
  };
};

type ActiveDafPayment = Exclude<inferRouterOutputs<AppRouterType>["getDafPayment"], { state: "deleted" }>;

const restoreCheckout = (data: ActiveDafPayment, current: DonationPaymentInput): DonationPaymentInput => {
  const checkout = data.checkout;
  return {
    ...current,
    firstName: checkout.firstName ?? "",
    lastName: checkout.lastName ?? "",
    email: checkout.email ?? "",
    address: checkout.address ?? "",
    city: checkout.city ?? "",
    region: checkout.region ?? "",
    postalCode: checkout.postalCode ?? "",
    country: checkout.country ? convertCountryCodeToCountryName(checkout.country) : undefined,
    companyName: checkout.corporationName ?? "",
    giveAsOrganism: checkout.isCorporate ?? false,
    amount: data.amount,
    manualAmount: String(data.amount / 100),
    recurrenceInterval: null,
    dafProviderId: data.details.providerId,
    answers:
      checkout.answers?.map((answer) => ({
        id: answer.id,
        questionId: answer.questionId,
        answers: answer.answer ? [answer.answer] : [],
        choiceIds: answer.choiceIds ?? [],
      })) ?? current.answers,
    isPublicDonation: !checkout.productDonation?.isAnonymous,
    inHonourOf: Boolean(checkout.productDonation?.tribute),
    tribute: checkout.productDonation?.tribute,
    inHonourName: checkout.productDonation?.inHonourName ?? "",
    inHonourNotifyEmail: checkout.productDonation?.inHonourEmailAddress ?? "",
    inHonourMessage: checkout.productDonation?.inHonourEmailBody ?? "",
    notifySomeone: checkout.productDonation?.sendInHonourEmail ?? false,
  };
};
